// Phase 68 D-04 / D-12: helper formatters for invoice period + compact dates.
// Single source of truth — used by InvoicesView, ContractCard (invoice card),
// AmbassadorsView. Backend has a parallel mirror at backend/src/utils/periodLabel.js
// for CSV export (intentional 8-line duplication, not worth a shared package).
//
// Convention diverges from backend mirror:
//   - frontend returns '—' (em dash) for null/undefined/empty       (UI display)
//   - backend  returns ''  (empty string) for null/undefined/empty (CSV cells)
//   - frontend returns String(input) for un-parseable strings       (visible debug
//     so a corrupt DB value renders to user instead of silent dash)
//   - backend  returns ''                for un-parseable strings   (machine-friendly)
//
// UTC anchoring: ISO date strings (YYYY-MM-DD) are appended with `T00:00:00Z`
// before parsing so west-of-UTC hosts can't slip the value back a calendar day.
// Date inputs use getUTC* accessors directly — same TZ guarantee.

const EN_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Format an ISO date (YYYY-MM-DD) into 'Month YYYY' English form.
 * Phase 68 D-04: replaces 'YYYY-MM-DD — YYYY-MM-DD' range display.
 *
 * Returns '—' for null/undefined/empty (frontend display convention).
 * Returns the input string if it cannot be parsed (graceful: surfaces a
 * corrupt DB value to the user instead of a silent dash).
 *
 * @param {string|Date|null|undefined} dateOrIso  e.g. '2026-04-01'
 * @returns {string}                               e.g. 'April 2026' or '—'
 */
export function formatMonthYear(dateOrIso) {
  if (!dateOrIso) return '—';
  const d = typeof dateOrIso === 'string'
    ? new Date(`${dateOrIso}T00:00:00Z`)
    : dateOrIso;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return String(dateOrIso);
  return `${EN_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Format an ISO date into dd.MM.yy compact form.
 * Phase 68 D-12: replaces 'YYYY-MM-DD' display in /invoices Дата column.
 *
 * Returns '—' for null/undefined/empty.
 * Returns the input string if it cannot be parsed (graceful, same rationale
 * as formatMonthYear).
 *
 * @param {string|Date|null|undefined} dateOrIso  e.g. '2026-04-29'
 * @returns {string}                               e.g. '29.04.26' or '—'
 */
export function formatCompactDate(dateOrIso) {
  if (!dateOrIso) return '—';
  const d = typeof dateOrIso === 'string'
    ? new Date(`${dateOrIso}T00:00:00Z`)
    : dateOrIso;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return String(dateOrIso);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
}
